"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ServiceUnavailableError = exports.GoneError = exports.ClientOutdatedError = exports.AuthError = exports.PermissionError = exports.UserFacingError = exports.RateLimitError = exports.ConflictError = exports.NotFoundError = exports.ValidationError = void 0;
// Phase 123 Plan 02 Task 1 — SEV3 subclasses (expected 4xx / user-facing edge cases).
//
// SEV3 = expected user-side condition. Sentry level `warning`. No alerting.
// The 7 classes carry their standard HTTP status codes so the Plan 04 filter
// can derive status from `AppError.httpStatus` without per-site overrides
// during Plan 09's hard cutover.
//
// PermissionError (403) + AuthError (401) are the planner-level refinement
// on top of the 5 classes CONTEXT.md enumerates — they close the HTTP-status
// preservation gap for `ForbiddenException` / `UnauthorizedException`
// migrations and mirror CLI Phase 108 naming so Plan 10 aligns automatically.
const base_1 = require("./base");
/** Input validation failure — 400 Bad Request. */
class ValidationError extends base_1.AppError {
    static severity = 'sev3';
    static httpStatus = 400;
}
exports.ValidationError = ValidationError;
/** Resource not found — 404. */
class NotFoundError extends base_1.AppError {
    static severity = 'sev3';
    static httpStatus = 404;
}
exports.NotFoundError = NotFoundError;
/** State conflict — 409 (already invited, duplicate, optimistic lock). */
class ConflictError extends base_1.AppError {
    static severity = 'sev3';
    static httpStatus = 409;
}
exports.ConflictError = ConflictError;
/** Rate-limit exceeded — 429. */
class RateLimitError extends base_1.AppError {
    static severity = 'sev3';
    static httpStatus = 429;
}
exports.RateLimitError = RateLimitError;
/**
 * Generic safe 4xx — a client-actionable failure whose `userMessage` is meant
 * to be READ by the caller. 400 is not cosmetic: the SanitizingExceptionFilter
 * resolves the wire status from this static, and the frontend deliberately
 * discards backend copy on any 5xx (frontend/src/lib/api-error.ts), so without
 * it every message here is swallowed and replaced by a generic fallback
 * (AIT-463). Upstream/provider failures belong in ServiceUnavailableError.
 */
class UserFacingError extends base_1.AppError {
    static severity = 'sev3';
    static httpStatus = 400;
}
exports.UserFacingError = UserFacingError;
/**
 * Authorization failure — 403 Forbidden. The caller is authenticated but not
 * permitted for this resource. Mirrors CLI Phase 108 naming; Plan 09 maps
 * NestJS `ForbiddenException` → this.
 */
class PermissionError extends base_1.AppError {
    static severity = 'sev3';
    static httpStatus = 403;
}
exports.PermissionError = PermissionError;
/**
 * Authentication failure — 401 Unauthorized. The caller is not authenticated
 * (or the token is invalid/expired). Mirrors CLI Phase 108 naming; Plan 09
 * maps NestJS `UnauthorizedException` → this.
 */
class AuthError extends base_1.AppError {
    static severity = 'sev3';
    static httpStatus = 401;
}
exports.AuthError = AuthError;
/**
 * Client (CLI or skill) below the server-enforced `min_required` threshold.
 * Maps to HTTP 426 Upgrade Required (RFC 9110 §15.5.16). The `details`
 * payload carries the structured `{ outdated, minVersions, messages }`
 * shape that the SanitizingExceptionFilter surfaces to the client; the
 * CLI's HTTP wrapper parses that shape, prints the messages, exits 1.
 */
class ClientOutdatedError extends base_1.AppError {
    static severity = 'sev3';
    static httpStatus = 426;
}
exports.ClientOutdatedError = ClientOutdatedError;
/**
 * Resource is gone / no longer applicable — 410 Gone. Used when an action is
 * blocked because the underlying state has been deliberately cleared (e.g.
 * tunnel reclaimed by destination-URL update or reaper sweep). CLI uses this
 * signal to exit cleanly without retry.
 */
class GoneError extends base_1.AppError {
    static severity = 'sev3';
    static httpStatus = 410;
}
exports.GoneError = GoneError;
/**
 * Service temporarily unavailable — 503. Used for deploy-window pauses and
 * other intentional temporary outages. Clients should retry after a delay.
 */
class ServiceUnavailableError extends base_1.AppError {
    static severity = 'sev3';
    static httpStatus = 503;
}
exports.ServiceUnavailableError = ServiceUnavailableError;
